import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';

import * as CameraActions from '../actions/camera.actions';
import { VehicleDvrVideoService } from '../../service/vehicale-dvr-video.serevice';
import { MatDialog } from '@angular/material/dialog';
import { cancelClipRequest } from '../actions';
import { NotificationService } from '@fc-core/services/notification.service';
import { getErrorMessage } from '@fc-core/services/notification.utils';
import { inject } from '@angular/core';

@Injectable()
export class CameraEffects {
  private notifications = inject(NotificationService);

  loadClipList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.loadClips),
      concatMap(({ vehicleId, upperDate, lowerDate }) =>
        this.vehicleDvrVideoService
          .getDvrClipByVehicleId(vehicleId, upperDate, lowerDate)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(cancelClipRequest))),
            map((data) =>
              CameraActions.loadClipsSuccess({
                data: data.list,
              }),
            ),
            catchError((error) =>
              of(CameraActions.loadCamerasFailure({ error })),
            ),
          ),
      ),
    );
  });

  getCurrentCameraInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.getCurrentCameraInfo),
      concatMap(({ cameraId }) =>
        this.vehicleDvrVideoService.getCameraInfo(cameraId).pipe(
          map((currentCameraInfo) =>
            CameraActions.getCurrentCameraInfoSuccess({ currentCameraInfo }),
          ),
          catchError((error) =>
            of(CameraActions.loadCamerasFailure({ error })),
          ),
        ),
      ),
    );
  });

  loadCurrentCameraStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.loadCurrentCameraStatus),
      concatMap(({ cameraId }) =>
        this.vehicleDvrVideoService.getCameraStatus(cameraId).pipe(
          map((cameraStatus) => {
            return CameraActions.loadCurrentCameraStatusSuccess({
              cameraState: cameraStatus,
            });
          }),
          catchError((error) =>
            of(CameraActions.loadCamerasFailure({ error })),
          ),
        ),
      ),
    );
  });

  getVideoRequestEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.getVideoRequestEvent),
      concatMap(({ camera, lower, upper, sources, title, description }) =>
        this.vehicleDvrVideoService
          .postVideoRequest(camera, lower, upper, sources, title, description)
          .pipe(
            map((data) => {
              this.dialog.closeAll();
              this.notifications.showSuccess(
                'Video request successfully created',
              );
              return CameraActions.getVideoRequestEventSuccess({ data: data });
            }),
            catchError((error) => {
              const errorMessage = getErrorMessage(error);
              this.notifications.showError(errorMessage);
              return of(CameraActions.loadCamerasFailure({ error }));
            }),
          ),
      ),
    );
  });

  loadCamerasVideoHistory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.getCameraVideoHistory),
      concatMap(({ cameraSerial }) =>
        this.vehicleDvrVideoService.getVideoHistory(cameraSerial).pipe(
          map((data) =>
            CameraActions.getCameraVideoHistorySuccess({
              videoHistory: data,
            }),
          ),
          catchError((error) => {
            return of(CameraActions.getCameraVideoHistoryError({ error }));
          }),
        ),
      ),
    );
  });

  updateDvrClip$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.updateVideoClip),
      concatMap(({ clip }) =>
        this.vehicleDvrVideoService.updateVideoClip(clip).pipe(
          map(() => {
            this.dialog.closeAll();
            this.notifications.showSuccess(
              'Video request successfully updated',
            );
            return CameraActions.updateVideoClipSuccess({
              clip: {
                id: clip.id,
                changes: {
                  id: clip.id,
                  title: clip.title,
                  description: clip.description,
                },
              },
            });
          }),
          catchError((error) =>
            of(CameraActions.loadCamerasFailure({ error })),
          ),
        ),
      ),
    );
  });

  deleteDvrClip$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CameraActions.deleteVideoClip),
      concatMap(({ clip }) =>
        this.vehicleDvrVideoService.deleteVideoClip(clip.id).pipe(
          map(() => {
            this.dialog.closeAll();
            this.notifications.showSuccess(
              'Video request successfully deleted',
            );
            return CameraActions.deleteVideoClipSuccess({
              clipId: clip.id,
            });
          }),
          catchError((error) =>
            of(CameraActions.loadCamerasFailure({ error })),
          ),
        ),
      ),
    );
  });

  constructor(
    private actions$: Actions,
    private vehicleDvrVideoService: VehicleDvrVideoService,
    private dialog: MatDialog,
  ) {}
}
